/**
 * iter-170t — WhatsAppActivateSheet
 *
 * BottomSheet shown when the user clicks "Activar +WhatsApp" on the
 * Integrations page. Replaces the hard navigation to /billing with an
 * in-place purchase pitch so the user keeps their context (and impulse)
 * intact. The actual checkout still lives on /pricing — this sheet just
 * frames the offer one tap closer.
 *
 * Why: page-level redirects mid-impulse cause measurable drop-off. A
 * BottomSheet keeps the connected-device flow visible behind the
 * scrim, anchors the price in the user's currency, and lets the user
 * either commit ("Continuar al pago") or back out without losing
 * their place. Aligns with the rest of the mobile-first nav.
 */
import { useNavigate } from "react-router-dom";
import { ArrowRight, MessageCircle, Camera, ShieldCheck } from "lucide-react";
import { Button } from "./ui/button";
import { BottomSheet } from "./ui/bottom-sheet";
import { tx } from "../i18n";
import { formatPrice } from "../lib/pricingResolver";

const VALUE_PROPS = [
  {
    icon: MessageCircle,
    es: "Registra ingresos y gastos con un mensaje de WhatsApp.",
    en: "Log income and expenses with a single WhatsApp message.",
    pt: "Registre receitas e despesas com uma mensagem do WhatsApp.",
  },
  {
    icon: Camera,
    es: "Envía fotos de recibos y Lumfi los procesa automáticamente.",
    en: "Send receipt photos and Lumfi processes them automatically.",
    pt: "Envie fotos de recibos e o Lumfi processa automaticamente.",
  },
  {
    icon: ShieldCheck,
    es: "Sin contraseña: tu número actúa como llave segura.",
    en: "No password — your phone number is your secure key.",
    pt: "Sem senha — seu número é a chave segura.",
  },
];

export function WhatsAppActivateSheet({
  open,
  onOpenChange,
  lang,
  planId,
  planName,
  priceEntry,
}) {
  const navigate = useNavigate();
  const priceLabel = priceEntry
    ? formatPrice(priceEntry.amount, priceEntry.currency, lang)
    : null;

  const handleContinue = () => {
    onOpenChange(false);
    if (planId) {
      navigate(`/pricing?highlight=${encodeURIComponent(planId)}`);
    } else {
      navigate("/pricing");
    }
  };

  return (
    <BottomSheet
      open={open}
      onOpenChange={onOpenChange}
      title={tx(lang, "Activate +WhatsApp", "Activar +WhatsApp", "Ativar +WhatsApp")}
      description={tx(lang,
        "Lumfi connects to your WhatsApp so you can log money on the go.",
        "Lumfi se conecta a tu WhatsApp para registrar movimientos al instante.",
        "O Lumfi se conecta ao seu WhatsApp para registrar movimentos na hora.")}
      testid="whatsapp-activate-sheet"
      size="md"
      footer={
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-end">
          <Button
            type="button"
            variant="ghost"
            onClick={() => onOpenChange(false)}
            data-testid="whatsapp-activate-sheet-dismiss"
          >
            {tx(lang, "Not now", "Ahora no", "Agora não")}
          </Button>
          <Button
            type="button"
            onClick={handleContinue}
            data-testid="whatsapp-activate-sheet-continue"
            className="bg-emerald-600 hover:bg-emerald-700 rounded-full gap-2"
          >
            {tx(lang, "Continue to checkout", "Continuar al pago", "Continuar para o checkout")}
            <ArrowRight className="h-4 w-4" />
          </Button>
        </div>
      }
    >
      <div className="space-y-5">
        {/* Price block — large, anchored in user's currency. */}
        {priceLabel && (
          <div
            className="rounded-2xl bg-emerald-50 border border-emerald-200 p-4 text-center"
            data-testid="whatsapp-activate-sheet-price"
          >
            <div className="text-[11px] uppercase tracking-wider text-emerald-700 font-semibold">
              {planName || tx(lang, "+WhatsApp add-on", "Addon +WhatsApp", "Addon +WhatsApp")}
            </div>
            <div className="mt-1 text-3xl font-bold text-slate-900 tabular-nums">
              {priceLabel}
              <span className="text-base font-normal text-slate-500 ml-1">
                / {tx(lang, "month", "mes", "mês")}
              </span>
            </div>
            <p className="text-[11px] text-slate-500 mt-1">
              {tx(lang,
                "Cancel anytime. No setup fee.",
                "Cancelable cuando quieras. Sin costo de configuración.",
                "Cancele quando quiser. Sem taxa de setup.")}
            </p>
          </div>
        )}

        {/* Value props */}
        <ul className="space-y-3" data-testid="whatsapp-activate-sheet-benefits">
          {VALUE_PROPS.map((vp, idx) => {
            const Icon = vp.icon;
            const label = tx(lang, vp.en, vp.es, vp.pt);
            return (
              <li key={idx} className="flex items-start gap-3">
                <div className="h-8 w-8 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center flex-shrink-0">
                  <Icon className="h-4 w-4" />
                </div>
                <p className="text-sm text-slate-700 leading-snug pt-1">{label}</p>
              </li>
            );
          })}
        </ul>
      </div>
    </BottomSheet>
  );
}
